export type ListingPricingBreakdown = {
  basePriceCents: number;
  platformFeeCents: number;
  finalPriceCents: number;
};

export type CheckoutPricingBreakdown = {
  baseSubtotalCents: number;
  platformFeeCents: number;
  serviceFeeCents: number;
  subtotalBeforeTaxCents: number;
  taxCents: number;
  deliveryFeeCents: number;
  totalCents: number;
};

// Listing prices are merchandise only. Order-level fees are quoted by the server.

export function dollarsToPricingCents(value: number | string | null | undefined) {
  const normalized = Number(value || 0);
  if (!Number.isFinite(normalized) || normalized <= 0) return 0;
  return Math.round(normalized * 100);
}

export function calculatePlatformFeeAmount(_amountCents: number) {
  // Kept for legacy listing consumers: no fee can be inferred per item because
  // an order-level fixed charge and effective-dated policy require a server quote.
  return 0;
}

export function calculatePlatformFeeDollars(amountDollars: number) {
  return calculatePlatformFeeAmount(dollarsToPricingCents(amountDollars)) / 100;
}

export function calculateListingPricing(
  price: number | string | null | undefined
): ListingPricingBreakdown {
  const basePriceCents = dollarsToPricingCents(price);
  const platformFeeCents = calculatePlatformFeeAmount(basePriceCents);
  return {
    basePriceCents,
    platformFeeCents,
    finalPriceCents: basePriceCents + platformFeeCents,
  };
}

export function calculateCheckoutPricing({
  subtotalCents,
  deliveryFeeCents = 0,
  taxCents = 0,
  platformFeeCents,
}: {
  subtotalCents: number;
  deliveryFeeCents?: number;
  taxCents?: number;
  platformFeeCents: number;
}): CheckoutPricingBreakdown {
  const baseSubtotalCents = Math.max(0, Math.round(Number(subtotalCents || 0)));
  const normalizedDeliveryFeeCents = Math.max(0, Math.round(Number(deliveryFeeCents || 0)));
  const normalizedTaxCents = Math.max(0, Math.round(Number(taxCents || 0)));
  if (!Number.isSafeInteger(platformFeeCents) || platformFeeCents < 0) {
    throw new Error("Checkout requires an explicit server-calculated service fee in cents.");
  }
  const normalizedPlatformFeeCents = platformFeeCents;
  const subtotalBeforeTaxCents =
    baseSubtotalCents + normalizedPlatformFeeCents + normalizedDeliveryFeeCents;

  return {
    baseSubtotalCents,
    platformFeeCents: normalizedPlatformFeeCents,
    serviceFeeCents: normalizedPlatformFeeCents,
    subtotalBeforeTaxCents,
    taxCents: normalizedTaxCents,
    deliveryFeeCents: normalizedDeliveryFeeCents,
    totalCents: subtotalBeforeTaxCents + normalizedTaxCents,
  };
}

export function buildCheckoutFeeSummary({
  itemSubtotalCents,
  serviceFeeCents,
  deliveryFeeCents = 0,
  taxCents = 0,
}: {
  itemSubtotalCents: number;
  serviceFeeCents: number;
  deliveryFeeCents?: number;
  taxCents?: number;
}) {
  const pricing = calculateCheckoutPricing({
    subtotalCents: itemSubtotalCents,
    deliveryFeeCents,
    taxCents,
    platformFeeCents: serviceFeeCents,
  });

  return {
    itemSubtotalCents: pricing.baseSubtotalCents,
    serviceFeeCents: pricing.serviceFeeCents,
    taxCents: pricing.taxCents,
    deliveryFeeCents: pricing.deliveryFeeCents,
    totalCents: pricing.totalCents,
  };
}

export function withListingPricing<T extends { price?: number | string | null }>(
  listing: T
): T & {
  priceCents: number;
  platformFeeCents: number;
  finalPriceCents: number;
} {
  const pricing = calculateListingPricing(listing?.price);
  return {
    ...listing,
    priceCents: pricing.basePriceCents,
    platformFeeCents: pricing.platformFeeCents,
    finalPriceCents: pricing.finalPriceCents,
  };
}
